import { EventType } from "@/analytics/analytics-events";
import { InspectCaptureLocationProperties } from "@/analytics/generic-events-properties";
import { ModeNames } from "@/modes/mode";
import {
  useCurrentAreaIfAvailable,
  useCurrentSceneIfAvailable,
} from "@/modes/mode-data-context";
import { selectModeName } from "@/store/mode-selectors";
import { changeMode } from "@/store/mode-slice";
import {
  selectFlyWalkSceneFilter,
  setFlyWalkSceneFilter,
} from "@/store/modes/flywalk-mode-slice";
import { setActiveElement } from "@/store/selections-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import {
  selectCanReadCAD,
  selectCanReadPointCloud,
} from "@/store/subscription-features/subscription-features-selectors";
import { SceneFilter } from "@/types/scene-filter";
import {
  DollhouseIcon,
  DollhouseWarningIcon,
  FaroTooltip,
  LayoutSheetSmallIcon,
  RunningIcon,
  ToolButton,
  ToolGroup,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import { isIElementOdometryPathSection } from "@faro-lotv/ielement-types";
import { SxProps, Theme } from "@mui/material";
import { ReactNode, useCallback, type JSX } from "react";
import { PointCloudAddOnRequiredMessage } from "./permission-messages";
import { OverlayToolbar } from "./overlay-toolbar";

/**
 * @returns A mode toggle between overview, sheet, and walk mode.
 */
export function ModeSwitch(): JSX.Element {
  const activeMode = useAppSelector(selectModeName);
  const dispatch = useAppDispatch();

  const overviewToggleState = useOverviewToggleState();

  /** True if only GaussianSplats are available */
  const onlyGaussianSplats =
    overviewToggleState.hasGaussianSplats &&
    !overviewToggleState.hasPointCloud &&
    !overviewToggleState.hasCad;

  const currentScene = useCurrentSceneIfAvailable();
  const isWalkModeAvailable =
    !!currentScene?.paths.length ||
    !!currentScene?.panos.length ||
    (overviewToggleState.isEnabled && !onlyGaussianSplats);

  const currentWalkSceneFilter = useAppSelector(selectFlyWalkSceneFilter);
  const handleWalkModeTransition = useCallback(() => {
    let targetSceneFilter: SceneFilter = currentWalkSceneFilter;
    // Coming from overview the filter is transferred as-is: walk mode renders every filter overview can.
    if (activeMode !== "overview" && currentScene) {
      const pano =
        currentScene.panos.at(0)?.id ??
        currentScene.paths.filter(isIElementOdometryPathSection).at(0)?.id;
      if (pano) {
        // Set the pano as the active element so that it's selected in the tree side panel.
        dispatch(setActiveElement(pano));
      } else {
        assert(
          overviewToggleState.hasPointCloud || overviewToggleState.hasCad,
          "Walk mode enabled but no valid 360 photo, CAD or point cloud exist",
        );
        targetSceneFilter = overviewToggleState.hasPointCloud
          ? SceneFilter.PointCloud
          : SceneFilter.Cad;

        if (currentWalkSceneFilter !== targetSceneFilter) {
          dispatch(setFlyWalkSceneFilter(targetSceneFilter));
        }
      }
    }

    if (targetSceneFilter === SceneFilter.PointCloud) {
      Analytics.track<InspectCaptureLocationProperties>(
        EventType.inspectCaptureLocation,
        { type: "pointcloud" },
      );
    } else if (targetSceneFilter === SceneFilter.Pano) {
      Analytics.track<InspectCaptureLocationProperties>(
        EventType.inspectCaptureLocation,
        { type: "panoView" },
      );
    }
  }, [
    currentWalkSceneFilter,
    activeMode,
    currentScene,
    dispatch,
    overviewToggleState.hasPointCloud,
    overviewToggleState.hasCad,
  ]);

  return (
    <OverlayToolbar>
      <ToolGroup
        value={activeMode}
        onChange={(_, modeName: ModeNames | null) => {
          // modeName can be null when the user clicks on the already selected button
          if (!modeName) return;

          if (modeName === "overview" && !overviewToggleState.isEnabled) return;
          if (
            modeName === "walk" &&
            (!isWalkModeAvailable || activeMode === "split")
          ) {
            return;
          }
          if (activeMode !== modeName) {
            dispatch(changeMode(modeName));
          }
        }}
        orientation="vertical"
        exclusive
      >
        <FaroTooltip
          title={overviewToggleState.tooltipContent}
          placement="right"
          disableInteractive={!overviewToggleState.isTooltipInteractive}
        >
          <ToolButton
            value="overview"
            selected={activeMode === "overview"}
            disabled={!overviewToggleState.isEnabled}
            sx={overviewToggleState.sx}
            onClick={() => {
              Analytics.track(EventType.openModel3dView);
            }}
          >
            {overviewToggleState.icon}
          </ToolButton>
        </FaroTooltip>

        <FaroTooltip title="2D overview" placement="right">
          <ToolButton
            onClick={() => {
              Analytics.track(EventType.openSheetView);
            }}
            value="sheet"
            selected={activeMode === "sheet"}
          >
            <LayoutSheetSmallIcon />
          </ToolButton>
        </FaroTooltip>

        <FaroTooltip title="Walk" placement="right">
          <ToolButton
            value="walk"
            selected={activeMode === "walk" || activeMode === "split"}
            disabled={!isWalkModeAvailable}
            onClick={handleWalkModeTransition}
          >
            <RunningIcon />
          </ToolButton>
        </FaroTooltip>
      </ToolGroup>
    </OverlayToolbar>
  );
}

/** Current state for the 3d overview button */
type OverviewToggleState = {
  /** True if the button is enabled */
  isEnabled: boolean;

  /** True, when the tooltip is interactive */
  isTooltipInteractive: boolean;

  /** Content of the button tooltip */
  tooltipContent: ReactNode;

  /** Icon to use for the button */
  icon: ReactNode;

  /** Extra style to apply to the button */
  sx?: SxProps<Theme>;

  /** Check if the current selection has a valid point cloud */
  hasPointCloud: boolean;

  /** Check if the current selection has a valid cad */
  hasCad: boolean;

  /** Check if the current selection has a valid gaussian splats */
  hasGaussianSplats: boolean;
};

/** @returns the current state of the Overview Button based on the app state */
function useOverviewToggleState(): OverviewToggleState {
  const canReadCad = useAppSelector(selectCanReadCAD);
  const canReadPointCloud = useAppSelector(selectCanReadPointCloud);

  const currentScene = useCurrentSceneIfAvailable();
  const currentArea = useCurrentAreaIfAvailable();

  const hasCad = !!currentScene?.cad;
  const hasPointCloud =
    !!currentArea?.dataSessions3d.pointClouds.length ||
    !!currentArea?.dataSessions5d.length;
  const hasGaussianSplats = !!currentArea?.dataSessions3d.gaussianSplats.length;

  const isEnabled =
    (hasCad && canReadCad) ||
    (hasPointCloud && canReadPointCloud) ||
    hasGaussianSplats;

  const pcPermissionError = hasPointCloud && !canReadPointCloud;

  return {
    isEnabled,
    isTooltipInteractive: pcPermissionError,
    tooltipContent: pcPermissionError ? (
      <PointCloudAddOnRequiredMessage />
    ) : (
      "3D overview"
    ),
    icon: pcPermissionError ? <DollhouseWarningIcon /> : <DollhouseIcon />,
    sx: pcPermissionError ? { opacity: 1 } : undefined,
    hasPointCloud,
    hasCad,
    hasGaussianSplats,
  };
}
